/**
 * Network wrapper — 오프라인 감지 + 연결 종류 (wifi / cellular / none).
 *
 * 사용 예:
 *   const { connected, type } = useNetworkStatus();
 *   if (!connected) return <OfflineNotice />;
 */
import { useEffect, useState } from "react";
import { isNative, nativeWarn } from "./index";

export interface NetworkStatus {
  connected: boolean;
  type: "wifi" | "cellular" | "none" | "unknown";
}

const DEFAULT_STATUS: NetworkStatus = { connected: true, type: "unknown" };

export async function getNetworkStatus(): Promise<NetworkStatus> {
  if (!isNative()) {
    return {
      connected: typeof navigator === "undefined" ? true : navigator.onLine,
      type: "unknown",
    };
  }
  try {
    const { Network } = await import("@capacitor/network");
    const status = await Network.getStatus();
    return {
      connected: status.connected,
      type: (status.connectionType as NetworkStatus["type"]) ?? "unknown",
    };
  } catch (err) {
    nativeWarn("network:getStatus", err);
    return DEFAULT_STATUS;
  }
}

export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(DEFAULT_STATUS);

  useEffect(() => {
    let mounted = true;
    let removeListener: (() => void) | null = null;

    (async () => {
      // 초기값
      const initial = await getNetworkStatus();
      if (mounted) setStatus(initial);

      // listener
      if (isNative()) {
        try {
          const { Network } = await import("@capacitor/network");
          const handle = await Network.addListener("networkStatusChange", (s) => {
            if (mounted) {
              setStatus({
                connected: s.connected,
                type: (s.connectionType as NetworkStatus["type"]) ?? "unknown",
              });
            }
          });
          removeListener = () => handle.remove();
        } catch (err) {
          nativeWarn("network:addListener", err);
        }
      } else if (typeof window !== "undefined") {
        const onOnline = () => mounted && setStatus({ connected: true, type: "unknown" });
        const onOffline = () => mounted && setStatus({ connected: false, type: "none" });
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        removeListener = () => {
          window.removeEventListener("online", onOnline);
          window.removeEventListener("offline", onOffline);
        };
      }
    })();

    return () => {
      mounted = false;
      removeListener?.();
    };
  }, []);

  return status;
}
